import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useLanguage } from '../i18n/LanguageContext';
import { APP } from '../config/app';
import { tidyPlace } from '../utils/intertownDisplay';
import { registerAndroidBackHandler } from '../utils/androidBack';
import { withTimeout } from '../utils/intertownWait';
import { IconBack, IconInfo, IconPin, IconTarget } from './IntertownIcons';
import { createMap, formatPlaceLabel, getBestDevicePosition, mountPlaceAutocomplete, reverseGeocode } from '../services/maps';
import './IntertownLocationPicker.css';

const point = (p) => p?.lat != null && p?.lng != null && Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng))
  ? { lat: Number(p.lat), lng: Number(p.lng) } : null;

// A pin is placed at street level. A destination with nothing saved starts wide instead (search or
// pinch from there): street level in Pileru is no help when the rider is looking for another town.
const PIN_ZOOM = 16;
const openingZoom = (pickup, hasPlace, inTown) => (pickup ? PIN_ZOOM : hasPlace ? 15 : inTown ? 14 : 11);

export default function IntertownLocationPicker({
  kind, initialPlace = null, anchor = null, maxDistanceKm = null, townName = null, startAt = null, onConfirm, onClose,
}) {
  const { t } = useLanguage();
  const host = useRef(null);
  const search = useRef(null);
  const mapRef = useRef(null);
  const alive = useRef(false);
  // Two counters on purpose. `revision` retires address lookups when the pin moves. `gpsRun` retires a
  // GPS fix that is still on its way when the rider takes over by hand. A GPS attempt must not retire a
  // lookup: if the GPS then fails, nothing would ever finish that lookup and the sheet would hang.
  const revision = useRef(0);
  const gpsRun = useRef(0);
  const lastCenter = useRef(null);
  const pending = useRef(null);
  const debounce = useRef(null);
  const userSelected = useRef(false);
  const dragged = useRef(false);
  const callbacks = useRef({});
  const [place, setPlace] = useState(initialPlace);
  const [resolving, setResolving] = useState(false);
  const [moving, setMoving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState(null); // { kind: 'area' | 'address', text }
  const [gpsFailed, setGpsFailed] = useState(false);
  const [hint, setHint] = useState(true);
  const [mapError, setMapError] = useState(false);
  const [accuracyWarning, setAccuracyWarning] = useState(false);
  const pickup = kind === 'pickup';
  const inTown = !pickup && Boolean(townName);

  const resolvePoint = useCallback(async (coords, preferred = null) => {
    const next = point(coords);
    if (!next || !alive.current) return;
    clearTimeout(debounce.current);
    pending.current = next;
    lastCenter.current = next;
    const token = ++revision.current;
    setPlace(null);
    setMoving(false);
    setError(null);
    setGpsFailed(false);
    const origin = point(anchor);
    const limit = Number(maxDistanceKm);
    if (origin && maxDistanceKm != null && Number.isFinite(limit)) {
      const rad = (n) => n * Math.PI / 180;
      const a = Math.sin(rad(next.lat - origin.lat) / 2) ** 2
        + Math.cos(rad(origin.lat)) * Math.cos(rad(next.lat)) * Math.sin(rad(next.lng - origin.lng) / 2) ** 2;
      if (6371 * 2 * Math.asin(Math.sqrt(Math.min(1, a))) > limit) {
        setError({
          kind: 'area',
          text: pickup ? t('intertown.pickupOutsideArea', { max: limit })
            : inTown ? t('intertown.townSpotOutside', { town: townName, max: limit })
              : t('intertown.destinationOutsideRoute', { max: limit }),
        });
        setResolving(false);
        return;
      }
    }
    setResolving(true);
    try {
      const resolved = preferred || await withTimeout(reverseGeocode(next.lat, next.lng), 12000, 'address');
      if (alive.current && token === revision.current) setPlace(tidyPlace({ ...resolved, ...next }));
    } catch {
      if (alive.current && token === revision.current) setError({ kind: 'address', text: t('intertown.pickerAddressFailed') });
    } finally {
      if (alive.current && token === revision.current) setResolving(false);
    }
  }, [anchor, maxDistanceKm, pickup, inTown, townName, t]);

  const moveMapTo = useCallback((coords, minZoom = 0) => {
    const map = mapRef.current;
    if (!map) return;
    map.setCenter(coords);
    // Only ever zoom in: a rider already closer than a pin needs stays where they are.
    if (minZoom && !(Number(map.getZoom?.()) >= minZoom)) map.setZoom(minZoom);
  }, []);

  // The rider took over by hand (dragged, searched, tapped): a GPS fix still on its way must not move the pin.
  const stopLocating = useCallback(() => {
    gpsRun.current += 1;
    setLocating(false);
  }, []);

  const selectPlace = useCallback((next, { preferred = null, zoomIn = false } = {}) => {
    const coords = point(next);
    if (!coords) return;
    userSelected.current = true;
    stopLocating();
    setHint(false);
    setAccuracyWarning(false);
    moveMapTo(coords, zoomIn ? PIN_ZOOM : 0);
    resolvePoint(coords, preferred);
  }, [moveMapTo, resolvePoint, stopLocating]);

  const locate = useCallback(async () => {
    const run = ++gpsRun.current;
    setLocating(true);
    setGpsFailed(false);
    try {
      const position = await withTimeout(getBestDevicePosition({ maxWaitMs: 8000, targetAccuracyM: 25, requestPermission: true }), 11000, 'gps');
      if (!alive.current || run !== gpsRun.current) return;
      const coords = point(position);
      if (!coords) throw new Error('invalid_position');
      moveMapTo(coords, PIN_ZOOM);
      setAccuracyWarning(Number(position.accuracy) > 50);
      resolvePoint(coords);
    } catch {
      // Said in the sheet rather than in a toast, so it stays readable and does not pop up on every open.
      if (alive.current && run === gpsRun.current) setGpsFailed(true);
    } finally {
      if (alive.current && run === gpsRun.current) setLocating(false);
    }
  }, [moveMapTo, resolvePoint]);
  callbacks.current = { resolvePoint, selectPlace, locate, onClose };

  useEffect(() => {
    alive.current = true;
    let cancelled = false;
    let circle;
    const listeners = [];
    const oldOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement;
    document.body.style.overflow = 'hidden';
    host.current?.closest('.itp-screen')?.querySelector('button')?.focus();
    const unregister = registerAndroidBackHandler(() => { callbacks.current.onClose(); return true; });
    // Esc inside the search box closes Google's suggestion list, not the whole picker.
    const escape = (e) => { if (e.key === 'Escape' && !e.target?.closest?.('.itp-search')) callbacks.current.onClose(); };
    document.addEventListener('keydown', escape);
    (async () => {
      try {
        const center = pending.current || point(initialPlace) || point(startAt) || (pickup ? point(anchor) : null) || APP.mapCenter;
        const map = await withTimeout(createMap(host.current, center, {
          showServiceArea: false, zoom: openingZoom(pickup, Boolean(point(initialPlace)), inTown),
          restriction: { latLngBounds: { north: 38.5, south: 6, west: 68, east: 98.5 }, strictBounds: true },
        }), 15000, 'map');
        if (cancelled) return;
        mapRef.current = map;
        lastCenter.current = center;
        // Search can finish while the map library is loading. Keep the visible pin
        // aligned with that selection rather than the original service centre.
        if (userSelected.current && pending.current) {
          moveMapTo(pending.current, PIN_ZOOM);
          lastCenter.current = pending.current;
        }
        if (point(anchor) && Number(maxDistanceKm) > 0 && window.google?.maps?.Circle) {
          circle = new window.google.maps.Circle({ map, center: point(anchor), radius: Number(maxDistanceKm) * 1000,
            fillColor: '#00a86b', fillOpacity: 0.035, strokeColor: '#008659', strokeOpacity: 0.6, strokeWeight: 1.5, clickable: false });
        }
        listeners.push(map.addListener('dragstart', () => {
          userSelected.current = true;
          stopLocating();
          setHint(false);
          setGpsFailed(false);
          dragged.current = true;
          revision.current += 1;
          clearTimeout(debounce.current);
          setPlace(null);
          setMoving(true);
          setResolving(false);
          setAccuracyWarning(false);
        }));
        listeners.push(map.addListener('idle', () => {
          const c = map.getCenter();
          const coords = { lat: c.lat(), lng: c.lng() };
          const prev = lastCenter.current;
          if (!dragged.current && prev && Math.abs(prev.lat - coords.lat) < 0.000001 && Math.abs(prev.lng - coords.lng) < 0.000001) {
            setMoving(false);
            return;
          }
          dragged.current = false;
          revision.current += 1;
          setPlace(null);
          setMoving(true);
          setResolving(false);
          clearTimeout(debounce.current);
          debounce.current = setTimeout(() => callbacks.current.resolvePoint(coords), 400);
        }));
        listeners.push(map.addListener('click', (event) => {
          if (event.placeId) event.stop?.();
          callbacks.current.selectPlace({ lat: event.latLng.lat(), lng: event.latLng.lng() });
        }));
        if (!userSelected.current) {
          if (point(initialPlace)) callbacks.current.resolvePoint(initialPlace, initialPlace);
          else if (pickup) callbacks.current.locate();
        }
      } catch { if (!cancelled) setMapError(true); }
    })();
    return () => {
      cancelled = true;
      alive.current = false;
      revision.current += 1;
      clearTimeout(debounce.current);
      listeners.forEach((listener) => listener.remove?.());
      circle?.setMap(null);
      mapRef.current = null;
      document.body.style.overflow = oldOverflow;
      previousFocus?.focus?.();
      document.removeEventListener('keydown', escape);
      unregister();
    };
    // Parent remounts for each picker kind; callbacks stay current without rebuilding the map.
  }, []);

  // Depend on the numbers, not on `anchor`/`startAt`: the page builds new objects on every render, and each
  // new object would tear the search box down and mount it again, wiping whatever the rider was typing.
  const bias = point(startAt) || point(anchor) || APP.mapCenter;
  useEffect(() => {
    let cancelled = false;
    const element = search.current;
    mountPlaceAutocomplete(element, t('intertown.searchLocation'), (next, reason) => {
      if (cancelled) return;
      if (!next) { toast.error(t(reason === 'billingError' ? 'intertown.locationSearchUnavailable' : 'intertown.locationNotFound')); return; }
      callbacks.current.selectPlace(next, { preferred: next, zoomIn: true });
    }, { restrictToServiceArea: false, locationBias: { center: { lat: bias.lat, lng: bias.lng }, radius: inTown ? 20000 : 120000 } })
      .catch(() => { if (!cancelled) toast.error(t('intertown.locationSearchUnavailable')); });
    return () => { cancelled = true; element?.replaceChildren(); };
  }, [t, inTown, bias.lat, bias.lng]);

  const title = pickup ? t('intertown.pickerSetPickup')
    : inTown ? t('intertown.townSpotTitle', { town: townName }) : t('intertown.pickerSetDestination');
  const busy = moving || resolving || locating;
  // One main line. A problem is that line, not a second paragraph under a generic prompt.
  const headline = moving ? t(pickup ? 'intertown.pickerMoving' : 'intertown.pickerMovingDestination')
    : resolving ? t('intertown.resolvingLocation')
      : locating ? t('intertown.pickerLocating')
        : error ? error.text
          : place ? formatPlaceLabel(place, 100) : t('intertown.pickerSearchOrMove');
  // The pin-check line is also the warning for a weak GPS fix, so it is not shown twice.
  const rule = pickup && maxDistanceKm != null ? t('intertown.pickupRadiusNote', { max: maxDistanceKm })
    : inTown ? t('intertown.townSpotSubtitle', { town: townName, max: maxDistanceKm })
      : pickup ? (accuracyWarning ? null : t('intertown.pickerCheckPin')) : t('intertown.pickerFareAfter');
  return (
    <div className="itp-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <section className={`itp-screen${pickup ? '' : ' is-destination'}`}>
        <header className="itp-header">
          <button type="button" onClick={onClose} aria-label={t('intertown.closeLocationPicker')}><IconBack size={24} /></button>
          <h2>{title}</h2>
        </header>
        <div className="itp-search" ref={search} />
        <div className="itp-map-wrap">
          <div className="itp-map" ref={host} />
          {hint && !mapError && <div className="itp-map-hint">{t(pickup ? 'intertown.pickerMovePickup' : 'intertown.pickerMoveDestination')}</div>}
          <div className="itp-center-pin" aria-hidden="true"><IconPin size={46} /><i /></div>
          {mapError && <div className="itp-map-error" role="alert">{t('intertown.locationSearchUnavailable')}</div>}
          {pickup && <button type="button" className="itp-gps" onClick={locate} disabled={locating} aria-label={t('intertown.useMyLocation')}>
            <IconTarget size={22} /><span>{t('intertown.useMyLocation')}</span>
          </button>}
        </div>
        <footer className="itp-summary">
          <div className="itp-summary-body" aria-live="polite">
            <small>{t(pickup ? 'intertown.selectedPickup' : 'intertown.selectedDestination')}</small>
            <strong className={error && !busy ? 'is-error' : undefined}>{headline}</strong>
            {place?.address && !busy && !error && <p className="itp-address">{place.address}</p>}
            {gpsFailed && !locating && <p className="itp-warning">{t('intertown.currentLocationFailed')}</p>}
            {accuracyWarning && <p className="itp-warning">{t('intertown.pickerCheckPin')}</p>}
            {rule && !error && <div className="itp-rule"><IconInfo size={18} /><span>{rule}</span></div>}
            {error?.kind === 'address' && !busy && <button type="button" className="itp-retry" onClick={() => pending.current && resolvePoint(pending.current)}>{t('intertown.retry')}</button>}
          </div>
          <button type="button" className="itp-confirm" disabled={!place || busy || Boolean(error)} onClick={() => place && onConfirm(place)}>
            {t(pickup ? 'intertown.pickerConfirmPickup' : 'intertown.pickerConfirmDestination')}
          </button>
        </footer>
      </section>
    </div>
  );
}
